import Vue from "vue";
import { firstOrderDate, isBreakfast } from "../../../helpers/breakfast";

import moment from "moment";
import {
  buildPromoCartItem,
  evaluatePromoConditions,
  findPromoItem,
  isEligibleLine,
  isItemLevelPromo,
  isPromoItem,
  itemPromoSavings,
  lineBasePrice,
  linePromoBasePrice,
  lineTotal,
  promoBookingLimit,
  promoLinePrice,
  round2,
  setLineBasePrice
} from "@/utils/promoCode";

/** Re-prices every eligible line and remembers what it cost before. */
function applyItemPromo(state, rules) {
  state.items.forEach(line => {
    if (isPromoItem(line)) return;

    if (!isEligibleLine(line, rules)) {
      restorePromoLine(line);
      return;
    }

    if (line.promoOriginalBase === undefined)
      line.promoOriginalBase = lineBasePrice(line);

    setLineBasePrice(line, linePromoBasePrice(line, rules, line.promoOriginalBase));
  });

  const promo = findPromoItem(state.items);
  if (promo) promo.promoSavings = itemPromoSavings(state.items);
}

function restorePromoLine(line) {
  if (!line || line.promoOriginalBase === undefined) return;
  setLineBasePrice(line, line.promoOriginalBase);
  Vue.delete(line, "promoOriginalBase");
}

function dropPromo(state) {
  state.items.forEach(restorePromoLine);
  state.items = state.items.filter(line => !isPromoItem(line));
}

/**
 * Keeps the promo in sync with the cart. Drops it, and records why in
 * promoNotice, as soon as its conditions stop holding.
 */
function reconcilePromo(state) {
  if (!Array.isArray(state.items)) return;

  const promo = findPromoItem(state.items);
  if (!promo) {
    state.items.forEach(restorePromoLine);
    return;
  }

  const rules = promo.promoRules;
  const result = evaluatePromoConditions(state.items, rules);

  if (!result.ok) {
    state.promoNotice = { ...result, code: rules.code, rules };
    dropPromo(state);
    return;
  }

  if (isItemLevelPromo(rules)) {
    applyItemPromo(state, rules);
  } else {
    const price = promoLinePrice(state.items, rules);
    promo.sellPrice = price;
    promo.deliverPrice = price;
    promo.takeAwayPrice = price;
  }
}

export default {
  namespaced: true,

  state: {
    items: [],

    promoNotice: null,
    // set by the promo target flow, the menu picks it up and opens the booking dialog
    promoTargetRequest: null,

    cartTotalSum: 0,
    totalSum: 0,

    current: 0,

    customerData: null,

    selectedItem: [],

    isAlreadyDuplicatedForNextDays: false,

    voucher: null
  },

  getters: {
    promoNotice: state => state.promoNotice,
    promoTargetRequest: state => state.promoTargetRequest,
    promoItem: state => findPromoItem(state.items),
    hasPromoItem: state => (state.items || []).some(isPromoItem),
    appliedPromotion: state => {
      const promo = findPromoItem(state.items);
      return promo ? promo.promoRules : null;
    },
    /** What the customer saves, whichever family the promo belongs to. */
    promoDiscountAmount: state => {
      const promo = findPromoItem(state.items);
      if (!promo) return 0;
      return isItemLevelPromo(promo.promoRules)
        ? Number(promo.promoSavings || 0)
        : Math.abs(Number(promo.sellPrice || 0));
    },
    /** Cart value before any promo reduction, what minimum order values check. */
    subtotalWithoutPromo: state =>
      round2(
        (state.items || [])
          .filter(line => !isPromoItem(line))
          .reduce(
            (sum, line) =>
              sum +
              lineTotal(
                line,
                line.promoOriginalBase !== undefined
                  ? line.promoOriginalBase
                  : lineBasePrice(line)
              ),
            0
          )
      ),
    /** Same as subtotalWithoutPromo, with the voucher folded in like the pc cart shows it. */
    subtotalWithoutPromoWithVoucher: (state, getters) => {
      let total = getters.subtotalWithoutPromo;
      if (state.voucher && parseFloat(state.voucher.balance) > 0)
        total -= parseFloat(state.voucher.balance);
      return total;
    },
    /** While a booking promo is redeemed its item is capped, null for anything else. */
    promoBookingLimitFor: state => (itemId, excludeIndex = null) => {
      const promo = findPromoItem(state.items);
      return promoBookingLimit(
        state.items,
        promo ? promo.promoRules : null,
        itemId,
        excludeIndex
      );
    },
    voucher: state => state.voucher,
    is_AlreadyDuplicatedForNextDays: state =>
      state.isAlreadyDuplicatedForNextDays,
    getDeliveryInfo: state => {
      return state.deliveryInfo;
    },
    isCurrent: state => id => {
      if (id === state.current) {
        return true;
      } else return false;
    },
    selectedItem: state => state.selectedItem,

    cart(state) {
      if (Array.isArray(state.items)) {
        return state.items;
      }
      return [];
    },

    isCartEmpty(state) {
      if (
        !Array.isArray(state.items) ||
        (Array.isArray(state.items) && state.items.length === 0)
      ) {
        return true;
      } else {
        return false;
      }
    },
    itemDeposit: () => item => {
      return window.store?.getters["Config/enableDeposit"] ? item?.deposit?.price || 0 : 0;
    },
    //total price of item
    itemTotalPrice: (state, getters) => data => {
      let itemIndex = state.items.indexOf(data);

      const item = state.items[itemIndex];
      let total = 0;
      if (item) {
        //item extras total price
        var extrasTotalPrice = 0;

        extrasTotalPrice += item.garnish.reduce(
          (extras_acc, garnish) =>
            extras_acc + parseFloat(garnish.sell_price) * garnish.amount,
          0
        );

        total = (parseFloat(item.sellPrice) + extrasTotalPrice) * item.amount;
        total -= total * (item.discount / 100);
      }
      return total + (getters.itemDeposit(data) * data.amount);
    },

    cartTotalSum(state, getters) {
      let total = 0;

      state.items.forEach(item => {
        for (let x = 1; x <= item.amount; x++) {
          let itemPrice = 0;

          //CHECK MEALSIZE
          if (item.mealsize) {
            itemPrice = item.mealsize.sellPrice;
            //CHECK DISCOUNT
            /*if (item.discountPriceBrutto !== 0) {
              itemPrice = item.discountPriceBrutto;
            }*/
          } else {
            itemPrice = item.sellPrice;

            //CHECK DISCOUNT
            /*if (item.mealsize.discountPriceBrutto !== 0) {
              itemPrice = item.mealsize.discountPriceBrutto;
            }*/
          }

          //GARNISH
          if (item.garnish) {
            item.garnish.forEach(garnishItem => {
              let garnishItemPrice = 0;

              garnishItemPrice = garnishItem.sell_price * garnishItem.amount;

              itemPrice += garnishItemPrice;
            });
          }

          // apply discount;
          itemPrice -= itemPrice * (item.discount / 100);
          total += (itemPrice + (getters.itemDeposit(item) * item.amount));
        }
      });

      return total;
    },
    cartTotalSumWithVoucher(state, getters) {
      let total = getters.cartTotalSum;
      if (state.voucher && parseFloat(state.voucher?.balance) > 0)
        total -= parseFloat(state.voucher?.balance);

      return total;
    },
    totalSumWithVoucher(state, getters) {
      let total = getters.totalSum;

      if (state.voucher && parseFloat(state.voucher?.balance) > 0)
        total -= parseFloat(state.voucher?.balance);

      return total;
    },

    totalSum(state, getters) {
      let total = 0;

      state.items.forEach(item => {
        for (let x = 1; x <= item.amount; x++) {
          let itemPrice = 0;

          //itemPrice = item.sellPrice;

          //CHECK MEALSIZE

          if (item.mealsize) {
            itemPrice = item.mealsize.sellPrice;

            //CHECK DISCOUNT
            /*if (item.discountPriceBrutto !== 0) {
              itemPrice = item.discountPriceBrutto;
            }*/
          } else {
            itemPrice = item.sellPrice;

            //CHECK DISCOUNT
            /*if (item.mealsize.discountPriceBrutto !== 0) {
              itemPrice = item.mealsize.discountPriceBrutto;
            }*/
          }

          //GARNISH
          if (item.garnish) {
            item.garnish.forEach(garnishItem => {
              let garnishItemPrice = 0;

              garnishItemPrice = garnishItem.sell_price * garnishItem.amount;

              //DISCOUNT
              /*if (garnishItem.discountPriceBrutto !== 0) {
                garnishItemPrice =
                  garnishItem.discountPriceBrutto * garnishItem.amount;
              }*/

              itemPrice += garnishItemPrice;
            });
          }
          // apply discount;
          itemPrice -= itemPrice * (item.discount / 100);
          total += (itemPrice + (getters.itemDeposit(item) * item.amount));
        }
      });

      if (window.store.getters["Config/isDelivery"]) {
        if (window.store.getters["Config/deliveryInfo"]) {
          total += window.store.getters["Config/deliveryInfo"].deliveryCosts;
        }
      }

      return total;
    },
    customerData: state => state.customerData || {},
  },

  mutations: {
    setCustomerData(state, payload) {
      state.customerData = payload;
    },
    updateCustomerField(state, { field, value }) {
      if (!state.customerData) {
        state.customerData = {};
      }
      state.customerData[field] = value;
    },
    setVoucher(state, payload) {
      state.voucher = payload;
    },
    setOrderAsAlreadyDuplicated(state, payload) {
      state.isAlreadyDuplicatedForNextDays = payload;
    },
    replaceCart(state, payload) {
      state.items = payload;

      reconcilePromo(state);
    },
    setToCurrent(state, val) {
      state.current = val;
    },

    setSelectedItem(state, val) {
      state.selectedItem = val;
    },

    bookItem(state, data) {
      //in breakfast mode we've to set booking date if it provided else we'll take the start date as item date
      if (
        isBreakfast() &&
        !Object.prototype.hasOwnProperty.call(data, "date")
      ) {
        let itemDate = firstOrderDate();

        data = {
          ...data,
          date: itemDate
        };
      }

      let cartItem = state.items.find(cartItem => {
        if (cartItem.id !== data.id) {
          return false;
        }

        //in case we're in breakfast order mode we should check we're booking the same item in the same day
        //else we'll trait it as a new item (return false => not found)
        if (isBreakfast() && cartItem.date !== data.date) return false;

        /*if (cartItem.discount !== data.discount) {
          return false;
        }*/

        if (cartItem.mealsize) {
          if (cartItem.mealsize.id !== data.mealsize.id) {
            return false;
          }
        }
        if (cartItem.discount !== data.discount) {
          return false;
        }

        if (
          (Array.isArray(cartItem.garnish) &&
            cartItem.garnish.length !== 0 &&
            cartItem.garnish !== null) ||
          (Array.isArray(data.garnish) &&
            data.garnish.length !== 0 &&
            data.garnish !== null)
        ) {
          if (
            JSON.stringify(cartItem.garnish) !== JSON.stringify(data.garnish)
          ) {
            return false;
          }
        }

        return true;
      });

      if (!cartItem) {
        let itemToBook = {
          id: data.id,
          name: data.name,
          sellPrice: data.sellPrice,
          mealsize: data.mealsize,
          garnish: data.garnish,
          freetext: "",
          amount: data.amount,
          deliverPrice: data.deliverPrice,
          takeAwayPrice: data.takeAwayPrice,
          discount: data.discount,
          itemGroup: data.itemGroup,
          deposit: data.deposit,
          depositgroupID: data.depositgroupID,
          canBeSoldOncePerOrder: data.canBeSoldOncePerOrder || false
        };

        if (data.mealsize) {
          // update item to use meal size info instead of the default item
          itemToBook = {
            ...itemToBook,
            name: data.name + " " + data.mealsize.sizeName,
            sellPrice: data.mealsize.sellPrice,
            mealsize: data.mealsize
          };
        }

        // add date attribute in breakfast mode
        if (
          isBreakfast() &&
          Object.prototype.hasOwnProperty.call(data, "date") &&
          moment(data.date, "DD.MM.YYYY").isValid()
        ) {
          itemToBook = {
            ...itemToBook,
            date: data.date
          };
        }

        state.items.push(itemToBook);
      } else {
        cartItem.amount += data.amount;
      }

      reconcilePromo(state);
    },

    addPromoItem(state, rules) {
      state.items.push(buildPromoCartItem(rules, promoLinePrice(state.items, rules)));
      if (isItemLevelPromo(rules)) applyItemPromo(state, rules);
    },

    removePromoCode(state) {
      dropPromo(state);
    },

    clearPromoNotice(state) {
      state.promoNotice = null;
    },

    setPromoTargetRequest(state, payload) {
      state.promoTargetRequest = payload || null;
    },

    removeQTY(state, data) {
      //const item = state.items.find((cartItem) => cartItem.id === data.id);

      let itemIndex = state.items.indexOf(data);

      const item = state.items[itemIndex];

      if (item) {
        if (item.amount > 1) {
          item.amount--;

          this.getters[("Order/itemTotalPrice", data)];
          //this.commit("Order/itemTotalPrice", data);
        }
      }

      reconcilePromo(state);
    },

    addQTY(state, data) {
      //const item = state.items.find((cartItem) => cartItem.id === data.id);

      let itemIndex = state.items.indexOf(data);

      const item = state.items[itemIndex];

      if (item) {
        item.amount++;

        this.getters[("Order/itemTotalPrice", data)];
        //this.commit("Order/itemTotalPrice", data);
      }

      reconcilePromo(state);
    },

    deleteItem(state, data) {
      let itemIndex = state.items.indexOf(data);

      restorePromoLine(state.items[itemIndex]);
      Vue.delete(state.items, itemIndex);

      reconcilePromo(state);
    },

    setItemFreetext(state, data) {
      const item = state.items.find(cartItem => cartItem.id === data.id);

      if (item) {
        item.freetext = data.freetext;
      }
    },
    setItemPrices(state, data) {
      // the delivery/takeaway switch rewrites the base prices, so any promo
      // pricing is stale: forget the old base and let reconcile re-apply it
      state.items.forEach(item => Vue.delete(item, "promoOriginalBase"));

      if (data) {
        state.items.forEach(item => {
          if (!item.mealsize) item.sellPrice = item.deliverPrice;

          item.discount = item.itemGroup
            ? item.itemGroup["delivery discount"]
            : 0;
        });
      } else {
        state.items.forEach(item => {
          if (!item.mealsize) item.sellPrice = item.takeAwayPrice;

          item.discount = item.itemGroup
            ? item.itemGroup["takeaway discount"]
            : 0;
        });
      }

      reconcilePromo(state);
    },
    emptyCart(state) {
      state.items = [];
      state.selectedItem = null;
      state.promoNotice = null;
      state.promoTargetRequest = null;
    }
  },

  actions: {
    bookItem({ commit }, data) {
      commit("bookItem", data);
    },
    deleteItem({ commit }, data) {
      commit("deleteItem", data);
    },
    setItemFreetext({ commit }, data) {
      commit("setItemFreetext", data);
    },
    emptyCart({ commit }) {
      commit("emptyCart");
    },
    reconcileCart({ state, commit, rootGetters }) {
      const groups = rootGetters["ItemGroups/itemGroups"] || [];
      const availableIds = new Set();
      groups.forEach(group => {
        if (Array.isArray(group.items)) {
          group.items.forEach(item => availableIds.add(item.id));
        }
      });

      const kept = [];
      const removed = [];
      state.items.forEach(cartItem => {
        // the promo line is no menu item, it must survive this sweep
        if (availableIds.has(cartItem.id) || isPromoItem(cartItem)) {
          kept.push(cartItem);
        } else {
          removed.push(cartItem);
        }
      });

      if (removed.length > 0) {
        commit("replaceCart", kept);
      }
      return removed;
    }
  }
};
